<template>
  <v-slide-x-transition>
    <v-sheet
      v-if="isOpen && panelId"
      v-click-outside="clickOutsideConfig"
      class="secondary-palette elevation-4"
      color="bg_Greeny_Gray"
      role="dialog"
      :aria-label="panelTitle"
    >
      <!-- Header with MenuTitleBar -->
      <slot name="header" :title="panelTitle">
        <MenuLogo @logoClick="handleLogoClick" />
        <MenuTitleBar
          :title="panelTitle"
          @back="handleBack"
        />
      </slot>

      <!-- Dynamic Panel Content -->
      <div class="palette-body" ref="panelContent">
        <Suspense v-if="panelComponent">
          <template #default>
            <component
              :is="panelComponent"
              :ref="panelId"
            />
          </template>
          <template #fallback>
            <div class="loading-skeleton">
              <v-skeleton-loader
                type="list-item-avatar, list-item-three-line, list-item-three-line, list-item-three-line"
              />
            </div>
          </template>
        </Suspense>

        <!-- Fallback for unknown panels -->
        <div v-else class="panel-not-found">
          <v-icon size="48" color="grey">mdi-alert-circle-outline</v-icon>
          <p class="mt-2 text--secondary">Panel not found: {{ panelId }}</p>
        </div>
      </div>

    </v-sheet>
  </v-slide-x-transition>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  PANEL_COMPONENTS,
  isHierarchicalPanel,
  getDynamicTitleConfig,
  getItemById,
} from '@Share/config/menuConfig';
import MenuLogo from '@Share/components/MenuLogo.vue';
import MenuTitleBar from '@Share/components/MenuTitleBar.vue';

export default {
  name: 'SecondaryPalette',

  components: {
    MenuLogo,
    MenuTitleBar,
  },

  props: {
    /**
     * ID of the panel to display (e.g., 'tags', 'global-search')
     */
    panelId: {
      type: String,
      required: true,
    },

    /**
     * Whether the palette is visible
     */
    isOpen: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      editingTag: 'GET_EDITING_TAG',
      editingAmisTag: 'GET_EDITING_AMIS_TAG',
    }),

    ...mapGetters('GENERAL', {
      isPinned: 'GET_IS_PINNED',
    }),

    /**
     * Get the menu item config for this panel
     */
    panelConfig() {
      return getItemById(this.panelId);
    },

    /**
     * Dynamic panel title based on context
     * For hierarchical panels, shows the editing item name
     */
    panelTitle() {
      const dynamicConfig = getDynamicTitleConfig(this.panelId);

      if (dynamicConfig) {
        // Check for editing state
        if (this.panelId === 'tags' && this.editingTag) {
          return this.editingTag.name || dynamicConfig.fallback;
        }
        if (this.panelId === 'condition-map' && this.editingAmisTag) {
          return this.editingAmisTag.name || dynamicConfig.fallback;
        }
        return dynamicConfig.fallback;
      }

      // Use config title or fallback
      return this.panelConfig?.title || this.panelId;
    },

    /**
     * Lazy-loaded panel component
     */
    panelComponent() {
      const loader = PANEL_COMPONENTS[this.panelId];
      if (!loader) {
        return null;
      }

      // Return async component
      return () => ({
        component: loader(),
        loading: {
          template: '<div class="loading-placeholder"><v-progress-circular indeterminate /></div>',
        },
        error: {
          template: '<div class="error-placeholder">Failed to load panel</div>',
        },
        delay: 200,
        timeout: 10000,
      });
    },

    /**
     * Whether this panel has hierarchical navigation
     */
    isHierarchical() {
      return isHierarchicalPanel(this.panelId);
    },

    /**
     * Whether we're at the root level of a hierarchical panel
     */
    isAtRootLevel() {
      if (this.panelId === 'tags') {
        return !this.editingTag;
      }
      if (this.panelId === 'condition-map') {
        return !this.editingAmisTag;
      }
      return true;
    },

    /**
     * Click-outside directive config
     */
    clickOutsideConfig() {
      return {
        handler: this.onClickOutside,
        include: () => {
          const elements = [
            document.querySelector('#icon-strip-menu'),
            document.querySelector('.primary-palette'),
          ].filter(Boolean);
          return elements;
        },
      };
    },
  },

  watch: {
    /**
     * When panel changes, scroll to top
     */
    panelId() {
      this.$nextTick(() => {
        if (this.$refs.panelContent) {
          this.$refs.panelContent.scrollTop = 0;
        }
      });
    },
  },

  methods: {
    /**
     * Handle back button click
     * For hierarchical panels, navigate within the panel first
     */
    handleBack() {
      this.$store.dispatch('GENERAL/CLOSE_SECONDARY_PANEL');
      this.$emit('back');
    },

    /**
     * Handle logo click
     */
    handleLogoClick() {
      this.$store.dispatch('GENERAL/CLOSE_ALL_PALETTES');
      this.$emit('logo-click');
    },

    /**
     * Handle click outside
     */
    onClickOutside() {
      if (this.isPinned) {
        return;
      }

      // Close panel
      this.$store.dispatch('GENERAL/CLOSE_ALL_PALETTES');
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.secondary-palette {
  position: fixed;
  top: var(--ds-nav-top-bar-height);
  left: var(--ds-nav-icon-strip-width);
  width: var(--ds-nav-secondary-palette-width);
  height: calc(100vh - var(--ds-nav-top-bar-height));
  display: flex;
  flex-direction: column;
  z-index: 7;
  border-radius: 0 var(--ds-radius-md) var(--ds-radius-md) 0;
  overflow: hidden;

  // Slide animation
  &.v-enter-active,
  &.v-leave-active {
    transition: transform var(--ds-transition-slow);
    will-change: transform;
  }

  &.v-enter,
  &.v-leave-to {
    transform: translateX(-100%);
  }
}

.palette-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: var(--ds-space-xs);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.loading-skeleton {
  padding: var(--ds-space-md);
}

.panel-not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: var(--ds-space-lg);
  text-align: center;
}

// Mobile responsive
@media (max-width: 768px) {
  .secondary-palette {
    left: 0;
    width: 100vw;
    height: 100vh;
    top: 0;
    border-radius: 0;
    z-index: 100;
  }
}
</style>
